import React, { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  createContent,
  getContent,
  updateContent,
} from '@plone/volto/actions/content/content';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import {
  fileContent,
  filingNote,
  filingOptions,
  uploadMessage,
} from './filing';

const readBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(',')[1] ?? '');
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

// Controls for people who can edit the meet: null for everyone else. The
// edit action is only listed for those who may use it.
export const useMeetEditing = (meetPath, slots) => {
  const dispatch = useDispatch();
  const canEdit = useSelector((state) =>
    state.actions?.actions?.object?.some((action) => action.id === 'edit'),
  );
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  if (!canEdit) return null;

  const options = filingOptions(slots);
  const run = async (work, failure) => {
    setBusy(true);
    setMessage(null);
    try {
      const text = await work();
      await dispatch(getContent(meetPath));
      setMessage({ text });
    } catch {
      setMessage({ text: failure, failed: true });
    }
    setBusy(false);
  };

  return {
    busy,
    message,
    options,
    upload: (file, chosen) =>
      run(async () => {
        const saved = await dispatch(
          createContent(
            meetPath,
            fileContent(file, await readBase64(file), chosen),
            'meet-document-upload',
          ),
        );
        return uploadMessage(saved, chosen, options);
      }, `${file.name} didn't upload. Please try again.`),
    move: (item, tag) =>
      run(async () => {
        await dispatch(
          updateContent(flattenToAppURL(item['@id']), { meet_document: tag }),
        );
        const where = options.find((o) => o.id === tag).title;
        return `${item.title} is now under ${where}.`;
      }, `${item.title} didn't move. Please try again.`),
  };
};

export const UploadButton = ({ editing, slotId, children }) => {
  const input = useRef(null);
  return (
    <>
      <input
        ref={input}
        type="file"
        hidden
        onChange={(event) => {
          const [file] = event.target.files;
          // So choosing the same file again still fires
          event.target.value = '';
          if (file) editing.upload(file, slotId);
        }}
      />
      <button
        type="button"
        className="asaj-doc-upload"
        disabled={editing.busy}
        onClick={() => input.current.click()}
      >
        {children}
      </button>
    </>
  );
};

// Under each file: where it's shown, and whether that was automatic
export const FilingControl = ({ editing, item }) => {
  const note = filingNote(item.filed_by);
  return (
    <div className={`asaj-filing ${note ? 'has-note' : ''}`}>
      <label>
        Shown as{' '}
        <select
          value={item.meet_document}
          disabled={editing.busy}
          onChange={(event) => editing.move(item, event.target.value)}
        >
          {editing.options.map((option) => (
            <option key={option.id} value={option.id}>
              {option.title}
            </option>
          ))}
        </select>
      </label>
      {note && <span className="asaj-filing-note">{note}</span>}
    </div>
  );
};

export const EditingMessage = ({ editing }) =>
  editing.message ? (
    <p
      className={`asaj-filing-message ${editing.message.failed ? 'failed' : ''}`}
      role={editing.message.failed ? 'alert' : 'status'}
    >
      {editing.message.text}
    </p>
  ) : null;
